/*
 * wachtwoord.component.ts
 * 10-09-2026 - Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Claude (AI)
 *
 * Wachtwoord vergeten in de webapplicatie, met twee weergaven: je adres invullen om een link aan te
 * vragen, en achter die link een nieuw wachtwoord instellen. Deze applicatie heeft geen router, dus
 * de appcomponent bepaalt welke van de twee er staat en wat er daarna gebeurt.
 */

import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ApiService} from '../../services/api/api.service';

/**
 * De twee weergaven van dit scherm
 */
export type Wachtwoordweergave = 'aanvragen' | 'instellen';

/**
 * De minimale lengte van een wachtwoord. Het backend eist hetzelfde; dit is alleen om de gebruiker
 * niet voor niets op een foutmelding te laten wachten
 */
const MINLENGTE = 6;

@Component({
  standalone: true,
  selector: 'wachtwoord',
  templateUrl: './wachtwoord.component.html',
  styleUrls: ['./wachtwoord.component.scss'],
  imports: [FormsModule]
})
export class WachtwoordComponent implements OnInit {
  /**
   * Welke van de twee weergaven er staat
   */
  @Input() weergave: Wachtwoordweergave = 'aanvragen';

  /**
   * De uid en de code uit de link, alleen bij de weergave instellen
   */
  @Input() uid: string = null;
  @Input() code: string = null;

  /**
   * Gaat af als dit scherm klaar is: of er meteen een sessie is, en met welk e-mailadres de gebruiker
   * hier gewerkt heeft. Dat laatste hoort daarna in het inlogformulier te staan
   */
  @Output() klaar = new EventEmitter<{ingelogd: boolean, email: string}>();

  /**
   * Het ingevulde e-mailadres; we beginnen met dat van de vorige inlog
   */
  public email: string = (localStorage && localStorage.loginEmail) || '';

  /**
   * Het nieuwe wachtwoord, twee keer
   */
  public nieuw: string = '';
  public herhaling: string = '';

  /**
   * Waar zolang er een call loopt, zodat de knop niet twee keer werkt
   */
  public bezig: boolean = false;

  /**
   * Waar zodra we het backend gevraagd hebben of de link nog werkt, en het antwoord daarop
   */
  public gecontroleerd: boolean = false;
  public geldig: boolean = false;

  /**
   * De apiservice voor de calls naar het backend
   */
  private readonly api = inject(ApiService);

  async ngOnInit(): Promise<void> {
    if (this.weergave !== 'instellen') {
      return;
    }
    this.geldig = !!(this.uid && this.code) && await this.api.wachtwoordLinkCheck(this.uid, this.code);
    this.gecontroleerd = true;
  }

  /**
   * De minimale lengte, voor in het scherm
   */
  public get minlengte(): number {
    return MINLENGTE;
  }

  /**
   * Is het formulier zover dat we het mogen versturen?
   */
  public get klopt(): boolean {
    return this.nieuw.length >= MINLENGTE && this.nieuw === this.herhaling;
  }

  /**
   * Vraag een link aan. De bevestiging is voor iedereen gelijk: of er een account bij dit adres hoort
   * hoort niemand hier te kunnen aflezen
   */
  public async vraagAan(): Promise<void> {
    this.bezig = true;
    try {
      await this.api.wachtwoordVergeten(this.email);
      localStorage.loginEmail = this.email; //zodat het instelscherm straks het juiste adres toont
      //De zin over de wachttijd staat er altijd, ook als er niets geblokkeerd is: zo verklapt hij niet of
      //dit adres een account heeft, en ook niet of er net iets is aangevraagd
      this.api.toast('Als dit adres bij ons bekend is, is er een mail onderweg. Heb je net al een link'
        + ' aangevraagd? Die is dan nog geldig. Er gaat er maar één per paar minuten uit.', 'success');
      this.klaar.emit({ingelogd: false, email: this.email});
    } catch (errcode) {
      this.bezig = false;
    }
  }

  /**
   * Stel het nieuwe wachtwoord in. Hoort het e-mailadres bij het account, dan zijn we daarna meteen
   * ingelogd; anders gaat de gebruiker naar het inlogformulier
   */
  public async instellen(): Promise<void> {
    this.bezig = true;
    try {
      if (await this.api.wachtwoordInstellen(this.uid, this.code, this.nieuw, this.email)) {
        localStorage.loginEmail = this.email; //bewaren, zoals bij gewoon inloggen
        //ook als het inloggen meteen lukt hoort er te staan dát het wachtwoord gewijzigd is; de toast van
        //het inloggen zegt daar niets over
        this.api.toast('Je nieuwe wachtwoord is ingesteld.', 'success');
        this.klaar.emit({ingelogd: true, email: this.email});
        return;
      }
      this.api.toast('Je wachtwoord is ingesteld. Log in met je nieuwe wachtwoord.', 'success');
      this.klaar.emit({ingelogd: false, email: this.email});
    } catch (errcode) {
      this.bezig = false;
      //een dode link: toon de uitleg in plaats van een formulier dat toch niet werkt
      if (errcode === 'NO_AUTH') {
        this.geldig = false;
      }
    }
  }

  /**
   * Terug naar het inlogformulier zonder iets te doen
   */
  public terug(): void {
    this.klaar.emit({ingelogd: false, email: this.email});
  }
}
